import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Tag,
  TagSkeleton,
  SkeletonText,
  InlineLoading,
  Modal
} from 'carbon-components-react';
import { isIOS } from 'react-device-detect';
import Utils from 'utilities/helper.util';
import { phoneNumberFormatter } from 'utilities/validation';
import { Close20 } from '@carbon/icons-react';
import { SCREEN_SIZE } from 'types';
import { MapView } from './mapView';
import { isEmpty, isUndefined, isNil } from 'lodash';

type Props = {
  enquiryDetail: any;
  headerTitle: string;
  screenSize: string;
  isLoading: boolean;
  isModal: boolean;
  onClickClose?: any;
  referralStatus: string;
};

const ReferralEnquiryInformation: React.FC<Props> = ({
  enquiryDetail,
  referralStatus,
  headerTitle,
  screenSize,
  isLoading,
  isModal,
  onClickClose
}) => {
  const [showMapModal, setShowMapModal] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [isEnquiryCreatedToday, setIsEnquiryCreatedToday] = useState<boolean>(
    true
  );
  const [mapModalLoaded, setMapModalLoaded] = useState<boolean>(false);
  const handleModalState = () => {
    setShowMapModal(!showMapModal);
  };

  const splitToNextLine = (data: string) => {
    if (!data) return '-';
    const splitedData = data.split(',');
    if (splitedData.length === 0) return data;
    return splitedData.map((item, index) => (
      <React.Fragment key={index}>
        {item} <br />
      </React.Fragment>
    ));
  };

  const splitArrayToNextLine = (data: any) => {
    return data.map((item: string, index: number) => (
      <React.Fragment key={index}>
        {item} <br />
      </React.Fragment>
    ));
  };

  const formatEnquirerDetail = (enquirer: any) => {
    return (
      <React.Fragment>
        {`${enquirer.firstName}  ${enquirer.lastName}`} <br />
        {!isNil(enquirer.companyName) && !isEmpty(enquirer.companyName) && (
          <React.Fragment>
            {enquirer.companyName}
            <br />
          </React.Fragment>
        )}
        {`${Utils.getFullAddress(enquirer.Address)}`} <br />
        {enquirer.email} <br />
        {!isNil(enquirer.phoneNumber) && (
          <React.Fragment>
            {phoneNumberFormatter({
              phoneNumber: enquirer.phoneNumber
            }).getNationalNumberFormat()}
            <br />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  useEffect(() => {
    if (enquiryDetail) {
      if (enquiryDetail.createdAt) {
        const dateToday = dayjs().startOf('day').format('DD/MM/YYYY');
        const enquiryDateFormated = Utils.toLocalDateFormat(
          enquiryDetail.createdAt
        );
        if (dateToday !== enquiryDateFormated) {
          setIsEnquiryCreatedToday(false);
        }
      }
    }
  }, [enquiryDetail]);

  const toggleDetailsPanel = () => {
    setShowDetails(!showDetails);
  };

  return (
    <div className="referralEnquiryInformation">
      {isModal && (
        <div className="close-button-wrapper">
          <div className="close-button-container" onClick={onClickClose}>
            <Close20 />
          </div>
        </div>
      )}

      <div className="referralEnquiryInformation--header">
        <div className="referralEnquiryInformation--header-middle">
          <div
            className={`referralEnquiryInformation--header-middle-title ${
              isModal ? 'title-modal' : ''
            }`}
          >
            <div className="referralEnquiryInformation--header-middle-title-jobId">
              {!isLoading && enquiryDetail?.externalId ? (
                <Tag className="tag-jobId">Job {enquiryDetail?.externalId}</Tag>
              ) : (
                <TagSkeleton />
              )}
            </div>
            <div className="referralEnquiryInformation--header-middle-title-wording">
              {headerTitle}
            </div>
          </div>
          <div className="referralEnquiryInformation--header-middle-status">
            {referralStatus}
          </div>
        </div>
      </div>
      <div className="referralEnquiryInformation--details">
        <div className="referralEnquiryInformation--details-show visible">
          <div className="referralEnquiryInformation--details-show-info">
            <div className="referralEnquiryInformation--details-show-info-location">
              {!isLoading && enquiryDetail?.shape && (
                <div
                  className="referralEnquiryInformation--details-show-info-location-map"
                  onClick={handleModalState}
                >
                  <MapView
                    baseMapTheme={'light'}
                    enquiryShape={enquiryDetail?.shape}
                  />
                </div>
              )}
              <div className="referralEnquiryInformation--details-show-info-location-address">
                <div className="text-styling-header">JOB SITE </div>
                <div className="text-styling-body">
                  {!isLoading && enquiryDetail?.Address ? (
                    Utils.getFullAddress(enquiryDetail.Address)
                  ) : (
                    <SkeletonText />
                  )}
                </div>
              </div>
            </div>
            <div className="referralEnquiryInformation--details-show-info-job-dates">
              <div className="text-styling-header semi-bold">JOB DATES</div>
              <div className="text-styling-body">
                {!isLoading && enquiryDetail ? (
                  <div>
                    {Utils.toLocalDateFormat(enquiryDetail?.digStartAt)}{' '}
                    {screenSize !== SCREEN_SIZE.SM ? <br /> : ''}
                    {isIOS ? (
                      <span className="arrow-symbol">&#8594;</span>
                    ) : screenSize === SCREEN_SIZE.SM ? (
                      <span className="arrow-symbol">&#8594;</span>
                    ) : (
                      '⤷'
                    )}{' '}
                    {Utils.toLocalDateFormat(enquiryDetail?.digEndAt)}
                  </div>
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="referralEnquiryInformation--details-show-info-enquiry-date">
              <div className="text-styling-header">ENQUIRY DATE</div>
              <div className="text-styling-body">
                {!isLoading && enquiryDetail?.createdAt ? (
                  <div>
                    {isEnquiryCreatedToday ? (
                      <>
                        Today
                        {screenSize !== SCREEN_SIZE.SM ? <br /> : ' '}
                        {Utils.toLocalTimeFormat(enquiryDetail.createdAt)}
                      </>
                    ) : (
                      <>
                        {Utils.toLocalDateFormat(enquiryDetail.createdAt)}
                        {screenSize !== SCREEN_SIZE.SM ? <br /> : ' '}
                        {Utils.toLocalTimeFormat(enquiryDetail.createdAt)}
                      </>
                    )}
                  </div>
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="referralEnquiryInformation--details-show-info-notificationType">
              <div className="text-styling-header">
                ENQUIRER NOTIFICATION PREF
              </div>
              <div className="text-styling-body">
                {enquiryDetail?.notificationType &&
                !isEmpty(enquiryDetail.notificationType)
                  ? enquiryDetail.notificationType
                  : '-'}
              </div>
            </div>
          </div>
          <div className="referralEnquiryInformation--details-show-info-2">
            {showDetails ? (
              <div
                onClick={toggleDetailsPanel}
                className="referralEnquiryInformation--details-show-info-toggle"
              >
                Less detail
              </div>
            ) : (
              <div
                onClick={toggleDetailsPanel}
                className="referralEnquiryInformation--details-collapsed-wrapper"
              >
                {' '}
                More details ...
              </div>
            )}
          </div>
        </div>
        <div
          className={`referralEnquiryInformation--divider ${
            showDetails ? 'visible' : 'hide'
          }`}
        />
        <div
          className={`referralEnquiryInformation--details-collapsed ${
            showDetails ? 'visible' : 'hide'
          }`}
        >
          <div className="referralEnquiryInformation--details-show-more-info">
            <div className="referralEnquiryInformation--details-show-more-info-activity">
              <div className="text-styling-header semi-bold">ACTIVITY</div>
              <div className="text-styling-body">
                {!isLoading && enquiryDetail?.activityTypes ? (
                  splitArrayToNextLine(enquiryDetail.activityTypes)
                ) : (
                  <SkeletonText />
                )}
              </div>
              {/* </div> */}
            </div>
            <div className="referralEnquiryInformation--details-show-more-info-workspace">
              <div className="text-styling-header semi-bold">
                WORKPLACE LOCATION
              </div>
              <div className="text-styling-body">
                {!isLoading && enquiryDetail ? (
                  splitToNextLine(enquiryDetail.workplaceLocation)
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="referralEnquiryInformation--details-show-more-info-enquirer">
              <div className="text-styling-header">ENQUIRER</div>
              <div className="text-styling-body">
                {!isLoading && enquiryDetail?.User ? (
                  formatEnquirerDetail(enquiryDetail.User)
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="referralEnquiryInformation--details-show-more-info-onbehalf">
              <div className="text-styling-header">ON BEHALF OF</div>
              <div className="text-styling-body">
                {!isLoading &&
                !isUndefined(enquiryDetail?.WorkingForAuthority) ? (
                  enquiryDetail?.WorkingForAuthority?.name ||
                  enquiryDetail?.otherAuthorityName ||
                  'Private'
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="referralEnquiryInformation--details-show-more-info-notes">
              <div className="text-styling-header">NOTES</div>
              <div className="text-styling-body">
                {!isLoading && !isUndefined(enquiryDetail?.notes) ? (
                  enquiryDetail.notes
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        passiveModal
        onRequestClose={() => {
          handleModalState();
          setMapModalLoaded(false);
        }}
        open={showMapModal}
        className={`map-modal ${isModal ? 'map-modal-popup' : ''}`}
      >
        <div className="referralEnquiryInformation--map-modal">
          {showMapModal && (
            <>
              {!mapModalLoaded && (
                <InlineLoading
                  iconDescription="Loading"
                  description="Loading map ..."
                />
              )}
              <MapView
                baseMapTheme={'light'}
                enquiryShape={enquiryDetail?.shape}
                onMapLoaded={() => setMapModalLoaded(true)}
              />
            </>
          )}
        </div>
      </Modal>
    </div>
    // </div>
  );
};

export default ReferralEnquiryInformation;
